// src/pages/sysadmin/SysAdminDashboard.jsx
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Server, Database, Shield, Activity, Cpu, HardDrive,
  RefreshCw, AlertTriangle, CheckCircle, XCircle,
  Clock, Eye, Ban, Trash2, Users, Terminal,
  TrendingUp, TrendingDown, BarChart2, Filter, Search,
  ChevronLeft, ChevronRight, Download, Plus, LogIn, Cookie,
  UserCheck, UserX, LogOut
} from 'lucide-react';
import BackupPanel from './BackupPanel';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

// ─── SparkLine ────────────────────────────────────────────────────────────────
const SparkLine = ({ data = [], color = '#38bdf8', height = 40, width = 140 }) => {
  if (!data.length) return null;
  const max = Math.max(...data, 1);
  const min = Math.min(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 6) - 3;
    return `${x},${y}`;
  });
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="overflow-visible">
      <defs>
        <linearGradient id={`sg-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={`0,${height} ${pts.join(' ')} ${width},${height}`} fill={`url(#sg-${color.replace('#', '')})`} />
      <polyline points={pts.join(' ')} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
};

// ─── StatCard ─────────────────────────────────────────────────────────────────
const StatCard = ({ title, value, sub, icon: Icon, color, trend, sparkData, onClick }) => (
  <div
    onClick={onClick}
    className="relative bg-gray-800/60 border border-gray-700/60 rounded-xl p-4 overflow-hidden group hover:border-gray-600 transition-all duration-300 cursor-pointer"
  >
    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
      style={{ background: `radial-gradient(circle at 80% 20%, ${color}15 0%, transparent 60%)` }} />
    <div className="relative">
      <div className="flex items-start justify-between mb-2">
        <div>
          <p className="text-gray-400 text-xs font-medium uppercase tracking-wider">{title}</p>
          <p className="text-3xl font-bold text-white mt-1 tabular-nums" style={{ color }}>
            {typeof value === 'number' ? value.toLocaleString() : value}
          </p>
        </div>
        <div className="p-2 rounded-lg" style={{ background: `${color}20` }}>
          <Icon size={18} style={{ color }} />
        </div>
      </div>
      {sparkData && sparkData.length > 1 && (
        <div className="mt-2 opacity-70">
          <SparkLine data={sparkData} color={color} height={36} width={130} />
        </div>
      )}
      {sub && (
        <div className="flex items-center gap-1 mt-2">
          {trend === 'up' && <TrendingUp size={12} className="text-emerald-400" />}
          {trend === 'down' && <TrendingDown size={12} className="text-red-400" />}
          <span className={`text-xs ${trend === 'up' ? 'text-emerald-400' : trend === 'down' ? 'text-red-400' : 'text-gray-400'}`}>
            {sub}
          </span>
        </div>
      )}
    </div>
  </div>
);

// ─── StatusBadge ──────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const config = {
    up:       { label: 'UP',       color: 'bg-green-500/20 text-green-400',  icon: CheckCircle },
    down:     { label: 'DOWN',     color: 'bg-red-500/20 text-red-400',      icon: XCircle },
    degraded: { label: 'DEGRADED', color: 'bg-yellow-500/20 text-yellow-400', icon: AlertTriangle },
    active:   { label: 'Active',   color: 'bg-green-500/20 text-green-400',  icon: CheckCircle },
    blocked:  { label: 'Blocked',  color: 'bg-red-500/20 text-red-400',      icon: Ban },
    pending:  { label: 'Pending',  color: 'bg-yellow-500/20 text-yellow-400', icon: Clock }
  };
  const c = config[status] || config.up;
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${c.color}`}>
      <Icon className="w-3 h-3" /> {c.label}
    </span>
  );
};

// ─── EventBadge ──────────────────────────────────────────────────────────────
const EventBadge = ({ type }) => {
  const map = {
    login_success: { label: 'Succès',  color: 'bg-green-500/20 text-green-400' },
    failed_login:  { label: 'Échec',   color: 'bg-red-500/20 text-red-400' },
    logout:        { label: 'Logout',  color: 'bg-gray-500/20 text-gray-400' },
    brute_force:   { label: 'Brute',   color: 'bg-orange-500/20 text-orange-400' },
  };
  const c = map[type] || { label: type, color: 'bg-gray-600/20 text-gray-400' };
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${c.color}`}>{c.label}</span>;
};

// ─── MiniBar ─────────────────────────────────────────────────────────────────
const MiniBar = ({ value, max, color }) => (
  <div className="flex items-center gap-2">
    <div className="flex-1 h-1.5 bg-gray-700 rounded-full overflow-hidden">
      <div className="h-full rounded-full transition-all duration-700"
        style={{ width: `${Math.min((value / Math.max(max, 1)) * 100, 100)}%`, background: color }} />
    </div>
  </div>
);

// ─── DonutChart ───────────────────────────────────────────────────────────────
const DonutChart = ({ data = [], size = 120 }) => {
  const total = data.reduce((s, d) => s + d.value, 0) || 1;
  const r = 42;
  const circ = 2 * Math.PI * r;
  let offset = circ * 0.25;
  return (
    <svg width={size} height={size} viewBox="0 0 100 100">
      <circle cx="50" cy="50" r={r} fill="none" stroke="#1e293b" strokeWidth="14" />
      {data.map((d, i) => {
        const angle = (d.value / total) * circ;
        const el = (
          <circle key={i} cx="50" cy="50" r={r} fill="none"
            stroke={d.color} strokeWidth="14"
            strokeDasharray={`${angle} ${circ}`}
            strokeDashoffset={offset}
            strokeLinecap="round"
            style={{ transition: 'stroke-dasharray 1s ease' }}
          />
        );
        offset -= angle;
        return el;
      })}
      <text x="50" y="45" textAnchor="middle" fill="white" fontSize="13" fontWeight="bold">{total}</text>
      <text x="50" y="60" textAnchor="middle" fill="#94a3b8" fontSize="8">TOTAL</text>
    </svg>
  );
};

// ─── BarChartSVG ──────────────────────────────────────────────────────────────
const BarChartSVG = ({ data = [], color = '#38bdf8', color2 = null, labelKey = 'hour', valueKey = 'count', value2Key = null }) => {
  const maxVal = Math.max(...data.map(d => Math.max(d[valueKey] || 0, d[value2Key] || 0)), 1);
  const W = 400, H = 80, pad = 20;
  const barW = Math.max(3, (W - pad * 2) / (data.length * (value2Key ? 2.5 : 1.5)));

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H + 20}`} className="overflow-visible">
      {[0, 0.5, 1].map(t => (
        <line key={t} x1={pad} y1={H * (1 - t)} x2={W - pad} y2={H * (1 - t)}
          stroke="#334155" strokeWidth="1" strokeDasharray="3,3" />
      ))}
      {data.map((d, i) => {
        const slot = (W - pad * 2) / data.length;
        const x = pad + i * slot;
        const h = ((d[valueKey] || 0) / maxVal) * H;
        const h2 = value2Key ? ((d[value2Key] || 0) / maxVal) * H : 0;
        return (
          <g key={i}>
            <rect x={x} y={H - h} width={barW} height={h} fill={color} rx="2" opacity="0.85" />
            {value2Key && (
              <rect x={x + barW + 1} y={H - h2} width={barW} height={h2} fill={color2 || '#ef4444'} rx="2" opacity="0.85" />
            )}
            <text x={x + barW / 2} y={H + 14} textAnchor="middle" fill="#64748b" fontSize="7">
              {d[labelKey]}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

// ─── IPCard ───────────────────────────────────────────────────────────────────
const IPCard = ({ ip, attempts, emails, onBlock, onUnblock, isBlocked, blocking }) => (
  <div className="bg-gray-900/50 rounded-lg p-3 border border-gray-700/50 hover:border-red-500/30 transition-all">
    <div className="flex items-center justify-between">
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <code className="text-red-400 text-sm font-mono">{ip}</code>
          <span className="text-xs text-gray-500">{attempts} tentatives</span>
          {isBlocked && <StatusBadge status="blocked" />}
        </div>
        {emails && emails.length > 0 && (
          <p className="text-xs text-gray-500 mt-1 truncate max-w-[200px]">
            Emails: {emails.join(', ')}
          </p>
        )}
      </div>
      {!isBlocked ? (
        <button onClick={() => onBlock(ip)} disabled={blocking === ip}
          className="px-2 py-1 bg-red-500/20 hover:bg-red-500/30 rounded text-red-400 text-xs flex items-center gap-1">
          {blocking === ip
            ? <div className="w-3 h-3 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
            : <Ban className="w-3 h-3" />}
          Bloquer
        </button>
      ) : (
        <button onClick={() => onUnblock(ip)}
          className="px-2 py-1 bg-gray-700 hover:bg-gray-600 rounded text-white text-xs">
          Débloquer
        </button>
      )}
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════════════════════
const SysAdminDashboard = () => {
  const [loading, setLoading]         = useState(true);
  const [refreshing, setRefreshing]   = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [activeTab, setActiveTab]     = useState('security');
  const [filterHours, setFilterHours] = useState(24);
  const [blockingIP, setBlockingIP]   = useState(null);
  const [loginFilter, setLoginFilter] = useState('all'); // all | success | failed | logout

  // ─── Data state ─────────────────────────────────────────────────────────────
  const [failedLogins, setFailedLogins]   = useState([]);
  const [bruteForceIPs, setBruteForceIPs] = useState([]);
  const [blacklistedIPs, setBlacklistedIPs] = useState([]);
  const [dbStats, setDbStats]             = useState(null);
  const [healthChecks, setHealthChecks]   = useState([]);
  const [auditLogs, setAuditLogs]         = useState([]);
  const [loginHistory, setLoginHistory]   = useState([]);
  const [loginStats, setLoginStats]       = useState(null);
  const [activeSessions, setActiveSessions] = useState([]);
  const [cookieStats, setCookieStats]     = useState(null);

  // ─── Fetch ──────────────────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setRefreshing(true);
    try {
      const [
        failedRes, bruteRes, blacklistRes,
        dbRes, healthRes, auditRes,
        historyRes, statsRes, sessionsRes, cookieRes
      ] = await Promise.allSettled([
        axios.get(`${API_BASE}/sysadmin/security/failed-logins?hours=${filterHours}&limit=100`),
        axios.get(`${API_BASE}/sysadmin/security/brute-force-ips?threshold=5&hours=24`),
        axios.get(`${API_BASE}/sysadmin/security/ip-blacklist`),
        axios.get(`${API_BASE}/sysadmin/database/stats`),
        axios.get(`${API_BASE}/sysadmin/health`),
        axios.get(`${API_BASE}/sysadmin/audit-logs?limit=50`),
        axios.get(`${API_BASE}/sysadmin/security/login-history?hours=${filterHours}&limit=200`),
        axios.get(`${API_BASE}/sysadmin/security/login-stats?hours=${filterHours}`),
        axios.get(`${API_BASE}/sysadmin/security/active-sessions?limit=50`),
        axios.get(`${API_BASE}/sysadmin/security/cookie-stats`),
      ]);

      if (failedRes.status === 'fulfilled')   setFailedLogins(failedRes.value.data?.failed_logins || []);
      if (bruteRes.status === 'fulfilled')    setBruteForceIPs(bruteRes.value.data?.suspicious_ips || []);
      if (blacklistRes.status === 'fulfilled') setBlacklistedIPs(blacklistRes.value.data?.blacklisted_ips || []);
      if (dbRes.status === 'fulfilled')       setDbStats(dbRes.value.data);
      if (healthRes.status === 'fulfilled')   setHealthChecks(healthRes.value.data?.services || []);
      if (auditRes.status === 'fulfilled')    setAuditLogs(auditRes.value.data?.audit_logs || []);
      if (historyRes.status === 'fulfilled')  setLoginHistory(historyRes.value.data?.login_history || []);
      if (statsRes.status === 'fulfilled')    setLoginStats(statsRes.value.data);
      if (sessionsRes.status === 'fulfilled') setActiveSessions(sessionsRes.value.data?.active_sessions || []);
      if (cookieRes.status === 'fulfilled')   setCookieStats(cookieRes.value.data);
    } catch (error) {
      console.error('fetchAll error:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
      setLastRefresh(new Date());
    }
  }, [filterHours]);

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, 60000);
    return () => clearInterval(interval);
  }, [fetchAll]);

  // ─── Actions ────────────────────────────────────────────────────────────────
  const handleBlockIP = async (ipAddress) => {
    if (!window.confirm(`Bloquer l'IP ${ipAddress} ?`)) return;
    setBlockingIP(ipAddress);
    try {
      await axios.post(`${API_BASE}/sysadmin/security/ip-blacklist`, null, {
        params: { ip_address: ipAddress, reason: 'Tentatives de connexion suspectes', hours: 24 }
      });
      await fetchAll();
    } catch { alert('Erreur lors du blocage'); }
    finally { setBlockingIP(null); }
  };

  const handleUnblockIP = async (ipAddress) => {
    if (!window.confirm(`Débloquer l'IP ${ipAddress} ?`)) return;
    try {
      await axios.delete(`${API_BASE}/sysadmin/security/ip-blacklist/${ipAddress}`);
      await fetchAll();
    } catch { alert('Erreur lors du déblocage'); }
  };

  // ─── Dérivées ────────────────────────────────────────────────────────────────
  const serviceHealthData = healthChecks.map(s => ({
    label: s.service_name,
    value: s.status === 'up' ? 1 : 0,
    color: s.status === 'up' ? '#10b981' : '#ef4444'
  }));

  // Sparkline réelle depuis hourly_activity
  const hourlyActivity = loginStats?.hourly_activity || [];
  const successSparkline = hourlyActivity.map(h => h.success || 0);
  const failedSparkline  = hourlyActivity.map(h => h.failed  || 0);

  // Bar chart "peak hours" avec données réelles
  const peakHoursData = hourlyActivity.map(h => ({
    hour: new Date(h.hour).getHours() + 'h',
    success: h.success || 0,
    failed:  h.failed  || 0,
  }));

  // Filtre login history
  const filteredHistory = loginHistory.filter(l => {
    if (loginFilter === 'all') return true;
    if (loginFilter === 'success') return l.event_type === 'login_success';
    if (loginFilter === 'failed')  return l.event_type === 'failed_login';
    if (loginFilter === 'logout')  return l.event_type === 'logout';
    return true;
  });

  // Sessions actives par type depuis cookie stats
  const staffSessions  = cookieStats?.active_sessions_by_type?.find(t => t.user_type === 'staff')?.active_sessions  || 0;
  const clientSessions = cookieStats?.active_sessions_by_type?.find(t => t.user_type === 'client')?.active_sessions || 0;

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Terminal className="w-6 h-6 text-purple-400" />
            Infrastructure Dashboard
          </h1>
          <p className="text-gray-400 text-xs mt-0.5 flex items-center gap-1">
            <Clock size={11} />
            Last refresh: {lastRefresh.toLocaleTimeString()}
          </p>
        </div>
        <div className="flex gap-2">
          <div className="flex bg-gray-800 border border-gray-700 rounded-lg overflow-hidden text-xs">
            {[12, 24, 48, 72].map(h => (
              <button key={h} onClick={() => setFilterHours(h)}
                className={`px-3 py-1.5 transition-colors ${filterHours === h ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'}`}>
                {h}h
              </button>
            ))}
          </div>
          <button onClick={fetchAll} disabled={refreshing}
            className="p-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-400 hover:text-white transition-colors">
            <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* ── Tabs ───────────────────────────────────────────────────────────── */}
      <div className="border-b border-gray-700">
        <div className="flex gap-1">
          {[
            { id: 'security', label: 'Security',  icon: Shield,    color: 'text-red-400' },
            { id: 'logins',   label: 'Logins',    icon: LogIn,     color: 'text-emerald-400' },
            { id: 'cookies',  label: 'Sessions',  icon: Cookie,    color: 'text-cyan-400' },
            { id: 'database', label: 'Database',  icon: Database,  color: 'text-blue-400' },
            { id: 'health',   label: 'Health',    icon: Activity,  color: 'text-green-400' },
            { id: 'audit',    label: 'Audit',     icon: Eye,       color: 'text-purple-400' },
            { id: 'backup',   label: 'Backup',    icon: HardDrive, color: 'text-amber-400' },
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-all duration-200 border-b-2 ${
                activeTab === tab.id ? `${tab.color} border-current` : 'text-gray-400 hover:text-white border-transparent'
              }`}>
              <tab.icon size={14} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          TAB: SECURITY
      ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'security' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard title="Failed Logins"   value={failedLogins.length}
              sub={`${filterHours}h`} icon={Shield} color="#ef4444"
              sparkData={failedSparkline} />
            <StatCard title="Suspicious IPs"  value={bruteForceIPs.length}
              sub="Brute force" icon={AlertTriangle} color="#f59e0b" />
            <StatCard title="Blacklisted"     value={blacklistedIPs.length}
              sub="IPs bloquées" icon={Ban} color="#8b5cf6" />
            <StatCard title="Comptes ciblés"  value={[...new Set(failedLogins.map(l => l.user_email))].length}
              sub="Emails uniques" icon={Users} color="#38bdf8" />
          </div>

          {bruteForceIPs.length > 0 && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
              <h3 className="text-red-400 font-semibold mb-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> IPs suspectes (Brute Force)
              </h3>
              <div className="space-y-2">
                {bruteForceIPs.map(ip => (
                  <IPCard key={ip.ip_address} ip={ip.ip_address} attempts={ip.attempts}
                    emails={ip.attempted_emails}
                    isBlocked={blacklistedIPs.some(b => b.ip_address === ip.ip_address)}
                    onBlock={handleBlockIP} onUnblock={handleUnblockIP} blocking={blockingIP} />
                ))}
              </div>
            </div>
          )}

          {blacklistedIPs.length > 0 && (
            <div className="bg-gray-800/60 rounded-xl border border-gray-700 p-4">
              <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                <Ban className="w-4 h-4 text-red-400" /> IPs blacklistées
              </h3>
              <div className="space-y-2">
                {blacklistedIPs.map(ip => (
                  <div key={ip.ip_address} className="flex items-center justify-between bg-gray-900/50 rounded-lg p-3">
                    <div>
                      <code className="text-gray-300 text-sm">{ip.ip_address}</code>
                      <p className="text-xs text-gray-500">{ip.reason || 'Aucune raison'}</p>
                      {ip.expires_at && (
                        <p className="text-xs text-gray-500">Expire: {new Date(ip.expires_at).toLocaleString()}</p>
                      )}
                    </div>
                    <button onClick={() => handleUnblockIP(ip.ip_address)}
                      className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm">
                      Débloquer
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Bar chart réel succès vs échecs par heure */}
          {peakHoursData.length > 0 && (
            <div className="bg-gray-800/60 rounded-xl border border-gray-700/60 p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-white font-semibold text-sm flex items-center gap-2">
                  <Clock size={15} className="text-cyan-400" />
                  Activité par heure — {filterHours}h
                </h3>
                <div className="flex items-center gap-3 text-xs text-gray-400">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" /> Succès</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500 inline-block" /> Échecs</span>
                </div>
              </div>
              <BarChartSVG data={peakHoursData} color="#10b981" color2="#ef4444"
                labelKey="hour" valueKey="success" value2Key="failed" />
            </div>
          )}

          <div className="bg-gray-800/60 rounded-xl border border-gray-700 overflow-hidden">
            <div className="p-4 border-b border-gray-700">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <Shield className="w-4 h-4 text-red-400" />
                Tentatives de connexion échouées
              </h3>
            </div>
            <div className="overflow-x-auto max-h-96">
              <table className="w-full text-sm">
                <thead className="bg-gray-800 sticky top-0">
                  <tr className="text-gray-400 border-b border-gray-700">
                    <th className="p-3 text-left">IP Address</th>
                    <th className="p-3 text-left">Email</th>
                    <th className="p-3 text-left">Type</th>
                    <th className="p-3 text-left">Endpoint</th>
                    <th className="p-3 text-left">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700/50">
                  {failedLogins.map((log, idx) => (
                    <tr key={idx} className="hover:bg-gray-700/30">
                      <td className="p-3 font-mono text-xs text-gray-300">{log.ip_address}</td>
                      <td className="p-3 text-gray-400">{log.user_email || '-'}</td>
                      <td className="p-3">
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          log.user_type === 'staff' ? 'bg-blue-500/20 text-blue-400' : 'bg-gray-500/20 text-gray-400'
                        }`}>{log.user_type || 'unknown'}</span>
                      </td>
                      <td className="p-3 text-gray-500 text-xs">{log.endpoint || '-'}</td>
                      <td className="p-3 text-gray-500 text-xs">{new Date(log.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                  {failedLogins.length === 0 && (
                    <tr><td colSpan="5" className="p-8 text-center text-gray-500">Aucune tentative échouée</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          TAB: LOGINS — Historique complet (succès + échecs + logouts)
      ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'logins' && (
        <div className="space-y-6">
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard title="Connexions réussies"
              value={loginStats?.counts?.total_success || 0}
              icon={UserCheck} color="#10b981"
              sparkData={successSparkline}
              sub={`${filterHours}h`} trend="up" />
            <StatCard title="Connexions échouées"
              value={loginStats?.counts?.total_failed || 0}
              icon={UserX} color="#ef4444"
              sparkData={failedSparkline}
              sub={`${filterHours}h`} />
            <StatCard title="Logouts"
              value={loginStats?.counts?.total_logouts || 0}
              icon={LogOut} color="#94a3b8"
              sub={`${filterHours}h`} />
            <StatCard title="IPs uniques"
              value={loginStats?.counts?.unique_ips || 0}
              icon={Server} color="#f59e0b"
              sub={`${filterHours}h`} />
          </div>

          {/* Répartition staff / client */}
          {loginStats?.by_user_type?.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-800/60 rounded-xl border border-gray-700 p-4">
                <h3 className="text-white font-semibold text-sm mb-4 flex items-center gap-2">
                  <Users size={15} className="text-blue-400" />
                  Succès par type d'utilisateur
                </h3>
                <div className="space-y-3">
                  {loginStats.by_user_type.map(t => (
                    <div key={t.user_type}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-gray-300 capitalize">{t.user_type}</span>
                        <span className="text-white font-medium">{t.cnt}</span>
                      </div>
                      <MiniBar value={t.cnt} max={loginStats.counts?.total_success || 1}
                        color={t.user_type === 'staff' ? '#3b82f6' : '#10b981'} />
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gray-800/60 rounded-xl border border-gray-700 p-4">
                <h3 className="text-white font-semibold text-sm mb-4 flex items-center gap-2">
                  <BarChart2 size={15} className="text-purple-400" />
                  Top IPs actives
                </h3>
                <div className="space-y-2">
                  {(loginStats.top_ips || []).slice(0, 5).map(ip => (
                    <div key={ip.ip_address} className="flex items-center justify-between">
                      <code className="text-xs text-gray-300 font-mono">{ip.ip_address}</code>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400">{ip.requests} req</span>
                        {ip.failures > 0 && (
                          <span className="text-xs text-red-400">{ip.failures} fail</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Tableau historique avec filtre */}
          <div className="bg-gray-800/60 rounded-xl border border-gray-700 overflow-hidden">
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <LogIn className="w-4 h-4 text-emerald-400" />
                Historique des connexions ({filteredHistory.length})
              </h3>
              <div className="flex bg-gray-900 border border-gray-700 rounded-lg overflow-hidden text-xs">
                {[
                  { key: 'all',     label: 'Tous' },
                  { key: 'success', label: '✓ Succès' },
                  { key: 'failed',  label: '✗ Échecs' },
                  { key: 'logout',  label: '⇥ Logout' },
                ].map(f => (
                  <button key={f.key} onClick={() => setLoginFilter(f.key)}
                    className={`px-3 py-1.5 transition-colors ${loginFilter === f.key ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'}`}>
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="overflow-x-auto max-h-96">
              <table className="w-full text-sm">
                <thead className="bg-gray-800 sticky top-0">
                  <tr className="text-gray-400 border-b border-gray-700">
                    <th className="p-3 text-left">Événement</th>
                    <th className="p-3 text-left">Email</th>
                    <th className="p-3 text-left">Type</th>
                    <th className="p-3 text-left">IP</th>
                    <th className="p-3 text-left">Endpoint</th>
                    <th className="p-3 text-left">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700/50">
                  {filteredHistory.map((log, idx) => (
                    <tr key={idx} className="hover:bg-gray-700/30">
                      <td className="p-3"><EventBadge type={log.event_type} /></td>
                      <td className="p-3 text-gray-300 text-xs">{log.user_email || '-'}</td>
                      <td className="p-3">
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          log.user_type === 'staff'  ? 'bg-blue-500/20 text-blue-400' :
                          log.user_type === 'client' ? 'bg-emerald-500/20 text-emerald-400' :
                          'bg-gray-500/20 text-gray-400'
                        }`}>{log.user_type || '?'}</span>
                      </td>
                      <td className="p-3 font-mono text-xs text-gray-400">{log.ip_address || '-'}</td>
                      <td className="p-3 text-gray-500 text-xs truncate max-w-[120px]">{log.endpoint || '-'}</td>
                      <td className="p-3 text-gray-500 text-xs whitespace-nowrap">
                        {new Date(log.created_at).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                  {filteredHistory.length === 0 && (
                    <tr><td colSpan="6" className="p-8 text-center text-gray-500">Aucun événement</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          TAB: SESSIONS / COOKIES
      ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'cookies' && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard title="Sessions Staff"  value={staffSessions}
              icon={Shield}    color="#3b82f6" sub="Cookies actifs estimés" />
            <StatCard title="Sessions Client" value={clientSessions}
              icon={Users}     color="#10b981" sub="Cookies actifs estimés" />
            <StatCard title="Logouts (24h)"   value={cookieStats?.logouts_last_24h || 0}
              icon={LogOut}    color="#94a3b8" sub="Sessions fermées" />
            <StatCard title="Tokens révoqués" value={cookieStats?.revoked_tokens_last_24h || 0}
              icon={XCircle}   color="#f59e0b" sub="Blacklistés / Expirés" />
          </div>

          {/* Explication architecture cookies */}
          <div className="bg-gray-800/60 rounded-xl border border-gray-700 p-4">
            <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
              <Cookie size={15} className="text-cyan-400" />
              Architecture des cookies
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
              {[
                { name: 'access_token',  httpOnly: true,  ttl: '30 min', desc: 'JWT staff/client — Inaccessible JS' },
                { name: 'refresh_token', httpOnly: true,  ttl: '7 jours', desc: 'Token rotation — Path /auth/refresh' },
                { name: 'csrf_token',    httpOnly: false, ttl: '30 min', desc: 'Protection CSRF — Lu par JS → header X-CSRF-Token' },
                { name: 'user_role',     httpOnly: false, ttl: '7 jours', desc: 'Rôle visible JS pour redirect — No sensitive data' },
              ].map(c => (
                <div key={c.name} className="bg-gray-900/50 rounded-lg p-3 border border-gray-700/50">
                  <div className="flex items-center justify-between mb-1">
                    <code className="text-cyan-400 font-mono">{c.name}</code>
                    <span className={`px-2 py-0.5 rounded text-xs ${c.httpOnly ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                      {c.httpOnly ? 'HttpOnly' : 'JS-readable'}
                    </span>
                  </div>
                  <p className="text-gray-400">{c.desc}</p>
                  <p className="text-gray-500 mt-1">TTL: {c.ttl}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Sessions actives */}
          <div className="bg-gray-800/60 rounded-xl border border-gray-700 overflow-hidden">
            <div className="p-4 border-b border-gray-700">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                Sessions actives estimées ({activeSessions.length})
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                Derniers login_success sans logout ultérieur — les cookies HttpOnly ne sont pas directement lisibles côté serveur.
              </p>
            </div>
            <div className="overflow-x-auto max-h-80">
              <table className="w-full text-sm">
                <thead className="bg-gray-800 sticky top-0">
                  <tr className="text-gray-400 border-b border-gray-700">
                    <th className="p-3 text-left">Email</th>
                    <th className="p-3 text-left">Type</th>
                    <th className="p-3 text-left">IP</th>
                    <th className="p-3 text-left">Dernière activité</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700/50">
                  {activeSessions.map((s, idx) => (
                    <tr key={idx} className="hover:bg-gray-700/30">
                      <td className="p-3 text-gray-300 text-xs">{s.user_email}</td>
                      <td className="p-3">
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          s.user_type === 'staff' ? 'bg-blue-500/20 text-blue-400' : 'bg-emerald-500/20 text-emerald-400'
                        }`}>{s.user_type}</span>
                      </td>
                      <td className="p-3 font-mono text-xs text-gray-400">{s.ip_address}</td>
                      <td className="p-3 text-gray-500 text-xs">{new Date(s.last_activity).toLocaleString()}</td>
                    </tr>
                  ))}
                  {activeSessions.length === 0 && (
                    <tr><td colSpan="4" className="p-8 text-center text-gray-500">Aucune session active</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          TAB: DATABASE
      ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'database' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard title="DB Connections" value={dbStats?.connections || 0}
              icon={Database} color="#38bdf8" sub="Actives" />
            <StatCard title="Cache Hit Ratio" value={`${dbStats?.cache_hit_ratio || 0}%`}
              icon={HardDrive} color="#10b981" trend="up" />
            <StatCard title="Slow Queries" value={dbStats?.slow_queries_24h || 0}
              icon={Clock} color="#f59e0b" sub="24h" />
            <StatCard title="Database Size" value={dbStats?.database_size || '0 GB'}
              icon={Database} color="#8b5cf6" sub={dbStats?.postgres_version} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-800/60 rounded-xl border border-gray-700 p-4">
              <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
                <Database size={15} className="text-blue-400" /> PostgreSQL Information
              </h3>
              <div className="space-y-2">
                {[
                  ['Version', dbStats?.postgres_version],
                  ['Environment', dbStats?.environment || 'development'],
                  ['Last Check', lastRefresh.toLocaleString()],
                ].map(([label, value]) => (
                  <div key={label} className="flex justify-between py-2 border-b border-gray-700 last:border-0">
                    <span className="text-gray-400 text-sm">{label}</span>
                    <span className="text-white text-sm">{value || '—'}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gray-800/60 rounded-xl border border-gray-700 p-4">
              <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
                <Activity size={15} className="text-green-400" /> Performance Metrics
              </h3>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-gray-400">Cache Efficiency</span>
                    <span className="text-white">{dbStats?.cache_hit_ratio || 0}%</span>
                  </div>
                  <MiniBar value={dbStats?.cache_hit_ratio || 0} max={100} color="#10b981" />
                </div>
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-gray-400">Connection Usage</span>
                    <span className="text-white">{dbStats?.connections || 0}/100</span>
                  </div>
                  <MiniBar value={dbStats?.connections || 0} max={100} color="#38bdf8" />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          TAB: HEALTH
      ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'health' && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {healthChecks.map(service => (
              <div key={service.service_name} className="bg-gray-800/60 rounded-xl border border-gray-700 p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-400 text-sm capitalize">{service.service_name}</span>
                  <StatusBadge status={service.status} />
                </div>
                {service.response_time_ms && (
                  <p className="text-xs text-gray-500">{service.response_time_ms}ms</p>
                )}
                {service.error_message && (
                  <p className="text-xs text-red-400 mt-1 truncate">{service.error_message}</p>
                )}
                <p className="text-xs text-gray-600 mt-2">
                  {new Date(service.checked_at).toLocaleTimeString()}
                </p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-800/60 rounded-xl border border-gray-700 p-4">
              <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
                <Activity size={15} className="text-green-400" /> Service Availability
              </h3>
              <div className="flex justify-center">
                <DonutChart data={serviceHealthData} size={140} />
              </div>
            </div>
            <div className="bg-gray-800/60 rounded-xl border border-gray-700 p-4">
              <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
                <Clock size={15} className="text-cyan-400" /> Response Times
              </h3>
              <div className="space-y-2">
                {healthChecks.map(service => (
                  <div key={service.service_name} className="flex items-center justify-between">
                    <span className="text-gray-300 text-sm capitalize">{service.service_name}</span>
                    <span className={`text-sm font-mono ${
                      service.response_time_ms < 100 ? 'text-green-400' :
                      service.response_time_ms < 500 ? 'text-yellow-400' : 'text-red-400'
                    }`}>{service.response_time_ms ? `${service.response_time_ms}ms` : '—'}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          TAB: AUDIT
      ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'audit' && (
        <div className="bg-gray-800/60 rounded-xl border border-gray-700 overflow-hidden">
          <div className="p-4 border-b border-gray-700">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <Eye className="w-4 h-4 text-purple-400" /> Admin Audit Logs
            </h3>
          </div>
          <div className="overflow-x-auto max-h-96">
            <table className="w-full text-sm">
              <thead className="bg-gray-800 sticky top-0">
                <tr className="text-gray-400 border-b border-gray-700">
                  <th className="p-3 text-left">Admin</th>
                  <th className="p-3 text-left">Action</th>
                  <th className="p-3 text-left">Target</th>
                  <th className="p-3 text-left">IP</th>
                  <th className="p-3 text-left">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/50">
                {auditLogs.map((log, idx) => (
                  <tr key={idx} className="hover:bg-gray-700/30">
                    <td className="p-3 text-gray-300">{log.staff_email || 'Système'}</td>
                    <td className="p-3">
                      <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 rounded text-xs">{log.action}</span>
                    </td>
                    <td className="p-3 text-gray-400 text-xs">{log.target_type} {log.target_id?.slice(0, 8)}...</td>
                    <td className="p-3 font-mono text-xs text-gray-500">{log.ip_address || '-'}</td>
                    <td className="p-3 text-gray-500 text-xs">{new Date(log.created_at).toLocaleString()}</td>
                  </tr>
                ))}
                {auditLogs.length === 0 && (
                  <tr><td colSpan="5" className="p-8 text-center text-gray-500">Aucun log d'audit</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {activeTab === 'backup' && (<BackupPanel />
      )}
      <style>{`
        @keyframes fade-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fade-in { animation: fade-in 0.25s ease-out; }
      `}</style>
    </div>
  );
};

export default SysAdminDashboard;